import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'

const ACTION_SOURCE = 'AssistantConfigurationForm'

/**
 * Editor for {@link ents.core.agent.Configuration.Context} that reads and
 * writes directly to the configuration state.
 *
 * Pass `mode="agent"` for the agent-default tab or `mode="conversation"` for
 * the per-conversation override tab. All draft reads and dispatches are
 * handled internally.
 */
@ngCore.Component({
  imports: [
    ds.input.checkbox.Component,
    ds.input.code.Component,
    ds.input.textArea.Component,
    ds.button.Component,
    ds.loader.Component,
  ],
  selector: 'atn-assistant-configuration-form',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Exposes the entities namespace for template usage. */
  public readonly Ents = ents
  /** @see {@link state.assistant.configuration.Feature} */
  public readonly configFeature = ngCore.inject(
    state.assistant.configuration.Feature,
  )
  /** @see {@link state.dms.schema.Feature} */
  public readonly schemaFeature = ngCore.inject(state.dms.schema.Feature)
  /** Which scope this form instance edits. */
  public readonly modeSg = ngCore.input<'agent' | 'conversation'>('agent', {
    alias: 'mode',
  })

  /** Adds or removes a knowledge-base file from the active draft. */
  public toggleKnowledgeBaseFile(
    file: ents.core.agent.KnowledgeBase.File,
    selected: boolean,
  ): void {
    // Pick the draft for the form scope currently being edited.
    const mode = this.modeSg()
    const draft =
      mode === 'agent'
        ? this.configFeature.selectors.editingAgentDraft.asSignal()
        : this.configFeature.selectors.editingConversationDraft.asSignal()

    // Treat a missing field as an empty selection list.
    const current = draft?.knowledgeBaseFiles ?? []

    // Add the file when checked, avoiding duplicates by KB id + S3 URI.
    // Remove that same file identity when unchecked.
    const next = selected
      ? current.some(item =>
          ents.core.agent.KnowledgeBase.File.isEqual(item, file),
        )
        ? current
        : [...current, file]
      : current.filter(
          item => !ents.core.agent.KnowledgeBase.File.isEqual(item, file),
        )

    this.updateField('knowledgeBaseFiles', next.length ? next : undefined)
  }

  /** Updates a single field in the active draft. */
  public updateField<K extends keyof ents.core.agent.Configuration.Context>(
    key: K,
    value: ents.core.agent.Configuration.Context[K],
  ): void {
    const mode = this.modeSg()
    const draft =
      mode === 'agent'
        ? this.configFeature.selectors.editingAgentDraft.asSignal()
        : this.configFeature.selectors.editingConversationDraft.asSignal()
    this.configFeature.actions[
      mode === 'agent' ? 'setAgentDraft' : 'setConversationDraft'
    ]
      .from(ACTION_SOURCE)
      .dispatch({ context: { ...(draft ?? {}), [key]: value } })
  }

  /** Copies the agent draft into the conversation scope so the user has a starting point. */
  public requestOverride(): void {
    this.configFeature.actions.setConversationDraft
      .from(ACTION_SOURCE)
      .dispatch({
        context: {
          ...(this.configFeature.selectors.editingAgentDraft.asSignal() ?? {}),
        },
      })
  }
}
